/**
 * A farm's devices with their latest readings, as the farm home and the devices list show
 * them, and the copy saved on the phone for when there's no signal. No JSX, so it can be tested.
 */
import { api, ApiError, loadSnapshot, saveSnapshot, type Device, type Reading, type Site } from "./api.ts";

export interface Loaded {
  sites: Site[];
  devices: Device[];
  latest: Map<string, Reading[]>;
  /** Set when there's no signal and this is the copy saved on the phone at that time. */
  savedAt?: number;
}

/** Newest values for each device, a few at a time so a big account doesn't flood the server. */
async function latestFor(devices: Device[]): Promise<Map<string, Reading[]>> {
  const out = new Map<string, Reading[]>();
  const queue = [...devices];
  const worker = async () => {
    for (let d = queue.shift(); d; d = queue.shift()) {
      try {
        out.set(d.devEui, (await api.latest(d.devEui)).readings);
      } catch {
        out.set(d.devEui, []);
      }
    }
  };
  await Promise.all(Array.from({ length: 6 }, worker));
  return out;
}

/** Set once the server has said it has no GET /latest, so we don't ask it every minute. */
let noLatestAll = false;

/**
 * Newest values for the whole farm in one request (GET /latest): one round trip instead of
 * one per device, which on a phone with weak signal is the difference between seconds and a
 * minute. A server without it yet answers 404: then device by device, as before.
 */
async function latestOf(devices: Device[], accountId: string | undefined, asked?: ReturnType<typeof api.latestAll>): Promise<Map<string, Reading[]>> {
  if (!noLatestAll) {
    try {
      const { latest } = await (asked ?? api.latestAll(accountId));
      return new Map(devices.map((d) => [d.devEui, latest[d.devEui] ?? []]));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) noLatestAll = true;
      else if (e instanceof ApiError && (e.code === "offline" || e.status === 401)) throw e;
    }
  }
  return latestFor(devices);
}

export interface LoadedDevice {
  device: Device;
  readings: Reading[];
  /** Set when there's no signal and this is a copy saved on the phone: nothing can be switched. */
  savedAt?: number;
}

/**
 * One device with its newest readings, for the device page. With no signal: the copy saved at
 * the last good load of this device, or of its farm, marked with when it's from. The page then
 * turns its switches off, so the saved copy can never be mistaken for the device's real state.
 */
export async function loadDevice(eui: string): Promise<LoadedDevice> {
  try {
    const r = await api.latest(eui);
    saveSnapshot(`device.${eui}`, r);
    return r;
  } catch (e) {
    if (!(e instanceof ApiError && e.code === "offline")) throw e;
    const own = loadSnapshot<{ device: Device; readings: Reading[] }>(`device.${eui}`);
    const farm = loadSnapshot<{ devices: Device[]; latest: Array<[string, Reading[]]> }>("farm.mine");
    const fromFarm = farm?.value.devices.find((d) => d.devEui === eui);
    // Whichever was saved last (the farm home may have been opened after this page).
    if (fromFarm && (!own || farm!.at > own.at)) {
      return { device: fromFarm, readings: new Map(farm!.value.latest).get(eui) ?? [], savedAt: farm!.at };
    }
    if (own) return { ...own.value, savedAt: own.at };
    throw e;
  }
}

export async function loadAccount(accountId: string | undefined): Promise<Loaded> {
  const snapKey = `farm.${accountId ?? "mine"}`;
  try {
    // All three at once: on one bar of signal each round trip is the best part of a second, so
    // the readings aren't left waiting for the device list to come back first.
    const asked = noLatestAll ? undefined : api.latestAll(accountId);
    asked?.catch(() => {}); // looked at below; never an unhandled rejection meanwhile
    const [{ sites }, { devices }] = await Promise.all([api.sites(accountId), api.devices(accountId)]);
    const loaded = { sites, devices, latest: await latestOf(devices, accountId, asked) };
    saveSnapshot(snapKey, { sites, devices, latest: [...loaded.latest] });
    return loaded;
  } catch (e) {
    // No signal: the last readings saved on this phone, marked with when they're from, beat a
    // blank "can't reach". Screens that switch turn their switches off while showing it.
    const snap = e instanceof ApiError && e.code === "offline" ? loadSnapshot<{ sites: Site[]; devices: Device[]; latest: Array<[string, Reading[]]> }>(snapKey) : undefined;
    if (!snap) throw e;
    return { sites: snap.value.sites, devices: snap.value.devices, latest: new Map(snap.value.latest), savedAt: snap.at };
  }
}
